import { ChangeEvent, createElement, useCallback, useRef } from "react";
import { IMAGE_EXTENSIONS } from "src/utils/imageUpload";

// Every file input in the app picks an image. `accept` only narrows the dialog (the user can switch
// it to "All files"); the picked file is then checked by validateImageFile — except the tenant logo
// (TenantDetail/tabs/overview/widgets/logo), which uploads the picked file with no client-side
// check, so only `accept` and the server's extension check apply there.
const ACCEPT = IMAGE_EXTENSIONS.map((extension) => `.${extension}`).join(",");

export interface FileInputOptions {
    onSelect?(file: File);
}

export const useFileInput = (options: FileInputOptions = {}) => {
    const inputFileRef = useRef<HTMLInputElement>();

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (!event?.target) {
                return;
            }

            const file = (event.target.files || [])[0];

            // Cancelling the dialog is not a selection; the callers read `file.name` and `file.size`.
            if (file) {
                options?.onSelect?.(file);
            }

            event.target.value = null;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [options?.onSelect, inputFileRef?.current]
    );

    const openFileInputDialog = useCallback(() => {
        if (typeof inputFileRef?.current?.click !== "function") {
            return;
        }

        inputFileRef.current.click();
    }, []);

    return {
        openFileInputDialog,
        fileInputElement: createElement("input", {
            key: "inputfile",
            type: "file",
            accept: ACCEPT,
            style: { display: "none" },
            ref: inputFileRef,
            onChange: handleChange,
        }),
    };
};
